<template>
  <div class="profile-shell" :class="{ 'profile-shell--panel': inPanel }">
    <section v-if="!inPanel" class="profile-left">
      <PhotoCarousel :images="carouselImages" />
    </section>
    <section class="profile-right" :class="{ 'profile-right--panel': inPanel }">
      <div class="ps-wrap">

        <!-- ── LEFT NAV ── -->
        <nav class="ps-nav">
          <div class="ps-user-card" @click="triggerAvatarInput" title="Alterar foto">
            <div class="ps-ava">
              <img v-if="avatar" :src="$resolveUrl(avatar)" class="ps-ava-img" :alt="$t('profile.avatarAlt')" />
              <div v-else class="ps-ava-ph">{{ (name || 'U').charAt(0).toUpperCase() }}</div>
              <span class="ps-ava-badge">
                <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 2l1 1-5 5H4V7l5-5z"/><path d="M7.5 3.5l1 1"/></svg>
              </span>
            </div>
            <div class="ps-user-info">
              <p class="ps-user-name">{{ name || '—' }}</p>
              <p class="ps-user-handle">@{{ username }}</p>
            </div>
          </div>
          <input id="avatar-file" class="hidden-input" type="file" accept="image/*" @change="onAvatarSelected" />

          <div class="ps-tabs">
            <button class="ps-tab" :class="{ 'ps-tab--active': activeTab === 'info' }" @click="goTab('editar')">
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4"><circle cx="8" cy="5" r="2.5"/><path d="M3 13c0-3.3 2.2-5 5-5s5 1.7 5 5" stroke-linecap="round"/></svg>
              {{ $t('profile.editProfile') }}
            </button>
            <button class="ps-tab" :class="{ 'ps-tab--active': activeTab === 'security' }" @click="goTab('seguranca')">
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4"><rect x="3" y="7" width="10" height="7" rx="2"/><path d="M5 7V5a3 3 0 0 1 6 0v2" stroke-linecap="round"/><circle cx="8" cy="10.5" r="1" fill="currentColor" stroke="none"/></svg>
              Segurança
            </button>
            <button class="ps-tab" :class="{ 'ps-tab--active': activeTab === 'email' }" @click="goTab('email')">
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4"><rect x="2" y="4" width="12" height="8" rx="1.5"/><path d="M2 5.5l6 4 6-4" stroke-linecap="round"/></svg>
              E-mail
            </button>
            <button class="ps-tab" :class="{ 'ps-tab--active': activeTab === 'appearance' }" @click="goTab('aparencia')">
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4"><circle cx="8" cy="8" r="3.5"/><path d="M8 1v1M8 14v1M1 8h1M14 8h1M3.05 3.05l.7.7M12.25 12.25l.7.7M3.05 12.95l.7-.7M12.25 3.75l.7-.7" stroke-linecap="round"/></svg>
              {{ $t('profile.appearance') }}
            </button>
            <button class="ps-tab" :class="{ 'ps-tab--active': activeTab === 'offline' }" @click="goTab('offline')">
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M1 1l14 14M11.2 4.8A5 5 0 0 1 13 8M2.5 5.5A7 7 0 0 0 3.5 12M6 3.2A7 7 0 0 1 13 8m-6.5 1A2.5 2.5 0 0 1 10.5 8M5.5 12l.5.5a2.5 2.5 0 0 0 3.5 0L10 12" stroke-linecap="round"/></svg>
              Offline
            </button>
            <button class="ps-tab" :class="{ 'ps-tab--active': activeTab === 'notifications' }" @click="goTab('notificacoes')">
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M8 1a5 5 0 0 1 5 5v3l1.5 2H1.5L3 9V6a5 5 0 0 1 5-5z" stroke-linecap="round" stroke-linejoin="round"/><path d="M6.5 13.5a1.5 1.5 0 0 0 3 0" stroke-linecap="round"/></svg>
              Notificações
            </button>
            <button class="ps-tab" :class="{ 'ps-tab--active': activeTab === 'timezone' }" @click="goTab('fuso')">
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4"><circle cx="8" cy="8" r="6.5"/><path d="M8 4.5V8l2.5 1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              Fuso Horário
            </button>
            <button class="ps-tab" :class="{ 'ps-tab--active': activeTab === 'versions' }" @click="goTab('versoes')">
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4"><polygon points="8 1 10.18 5.41 15 6.18 11.5 9.59 12.36 14.41 8 12.11 3.64 14.41 4.5 9.59 1 6.18 5.82 5.41 8 1"/></svg>
              Versões
            </button>
          </div>
        </nav>

        <!-- ── RIGHT CONTENT ── -->
        <div class="ps-content">
          <ProfileInfoTab
            v-if="activeTab === 'info'"
            :name="name"
            :username="username"
            :error="error"
            @update:name="name = $event"
            @update:username="username = $event"
            @save="save"
            @cancel="cancel"
          />
          <ProfileSegurancaTab v-else-if="activeTab === 'security'" />
          <ProfileEmailTab v-else-if="activeTab === 'email'" />
          <ProfileAparenciaTab v-else-if="activeTab === 'appearance'" />
          <ProfileOfflineTab v-else-if="activeTab === 'offline'" />
          <ProfileNotificacoesTab v-else-if="activeTab === 'notifications'" />
          <ProfileFusoTab v-else-if="activeTab === 'timezone'" />
          <ProfileVersoesTab v-else-if="activeTab === 'versions'" />
        </div>

      </div><!-- /ps-wrap -->
    </section>

    <!-- ── CROP MODAL ── -->
    <div v-if="cropOpen" class="crop-overlay" @mousedown.self="cancelCrop">
      <div class="crop-modal">
        <h3 class="crop-title">{{ $t('profile.cropTitle') }}</h3>
        <div class="crop-stage" ref="stageRef" @mousedown="onStageDragStart" @touchstart.prevent="onStageTouchStart">
          <img ref="cropImgRef" :src="cropSrc" class="crop-img" :style="{ transform: `translate(${imgX}px, ${imgY}px) scale(${imgScale})`, transformOrigin: '0 0' }" draggable="false" />
          <div class="crop-mask">
            <div class="crop-box" :style="{ width: cropBoxSize + 'px', height: cropBoxSize + 'px' }">
              <div class="crop-corner tl"></div>
              <div class="crop-corner tr"></div>
              <div class="crop-corner bl"></div>
              <div class="crop-corner br"></div>
            </div>
          </div>
        </div>
        <div class="crop-zoom-row">
          <span class="crop-zoom-label">{{ $t('profile.zoom') }}</span>
          <input type="range" min="1" max="4" step="0.01" v-model.number="zoomMultiplier" class="crop-zoom-slider" />
        </div>
        <div class="crop-actions">
          <button class="btn btn-ghost btn-login" type="button" @click="cancelCrop">{{ $t('common.cancel') }}</button>
          <button class="btn btn-primary btn-login" type="button" @click="confirmCrop">{{ $t('profile.cropUse') }}</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref, nextTick, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { navigationStore } from '@/store/navigation'
import { useI18n } from 'vue-i18n'
import { accountsStore } from '@/store/accounts'
import { sessionStore } from '@/store/session'
import { apiFetch } from '@/store/api'
import PhotoCarousel from '@/components/PhotoCarousel.vue'
import ProfileInfoTab from '@/components/profile/ProfileInfoTab.vue'
import ProfileSegurancaTab from '@/components/profile/ProfileSegurancaTab.vue'
import ProfileEmailTab from '@/components/profile/ProfileEmailTab.vue'
import ProfileAparenciaTab from '@/components/profile/ProfileAparenciaTab.vue'
import ProfileOfflineTab from '@/components/profile/ProfileOfflineTab.vue'
import ProfileNotificacoesTab from '@/components/profile/ProfileNotificacoesTab.vue'
import ProfileFusoTab from '@/components/profile/ProfileFusoTab.vue'
import ProfileVersoesTab from '@/components/profile/ProfileVersoesTab.vue'

const TARGET_SIZE_BYTES = 200 * 1024
const CROP_OUTPUT_PX = 512

export default defineComponent({
  name: 'ProfileEditView',
  components: {
    PhotoCarousel,
    ProfileInfoTab, ProfileSegurancaTab, ProfileEmailTab,
    ProfileAparenciaTab, ProfileOfflineTab, ProfileNotificacoesTab, ProfileFusoTab, ProfileVersoesTab,
  },
  props: {
    inPanel: { type: Boolean, default: false }
  },
  emits: ['done'],
  setup(props, { emit }) {
    const router = useRouter()
    const route = useRoute()
    const { t } = useI18n()

    // ── Active tab from URL ──
    const activeTab = computed(() => {
      const p = route.path
      if (p.endsWith('/seguranca')) return 'security'
      if (p.endsWith('/email')) return 'email'
      if (p.endsWith('/aparencia')) return 'appearance'
      if (p.endsWith('/offline')) return 'offline'
      if (p.endsWith('/notificacoes')) return 'notifications'
      if (p.endsWith('/fuso')) return 'timezone'
      if (p.endsWith('/versoes')) return 'versions'
      return 'info'
    })

    const goTab = (slug: string) => {
      router.push(`/profile/${slug}`)
    }

    // ── Profile state (for user card + info tab) ──
    const name = ref('')
    const username = ref('')
    const avatar = ref('')
    const error = ref('')

    const loadProfile = async () => {
      const session = sessionStore.state.user
      if (!session) { router.push('/login'); return }
      name.value = session.name || ''
      username.value = session.username || ''
      avatar.value = session.avatar || ''
      await accountsStore.refresh().catch(() => { /* keep session data */ })
      const account = accountsStore.all().find(
        a => (session.uid && a.uid === session.uid) || a.username === session.username
      )
      if (account) {
        name.value = account.name || name.value
        username.value = account.username || username.value
        avatar.value = account.avatar || avatar.value
      }
    }

    const save = async () => {
      error.value = ''
      if (!name.value.trim() || !username.value.trim()) {
        error.value = t('profile.saveFillAll')
        return
      }
      const session = sessionStore.state.user
      if (!session) { router.push('/login'); return }
      try {
        await accountsStore.upsert({
          uid: session.uid,
          name: name.value.trim(),
          username: username.value.trim(),
          password: undefined,
          role: session.role,
          roleId: session.roleId || '',
          subordinateUsernames: session.subordinateUsernames || [],
          hasSubordinates: Boolean(session.hasSubordinates),
          avatar: avatar.value
        })
        await accountsStore.refresh().catch(() => { /* keep session data */ })
        const updated = accountsStore.all().find(a => a.username === username.value.trim())
        sessionStore.login({
          name: updated?.name || name.value.trim(),
          role: updated?.role || session.role,
          username: updated?.username || username.value.trim(),
          avatar: updated?.avatar || avatar.value,
          uid: updated?.uid || session.uid,
          roleId: updated?.roleId || session.roleId || '',
          subordinateUsernames: updated?.subordinateUsernames || session.subordinateUsernames || [],
          hasSubordinates: updated?.hasSubordinates ?? session.hasSubordinates
        })
        if (props.inPanel) emit('done')
        else router.push('/home')
      } catch (err: unknown) {
        error.value = err instanceof Error ? err.message : t('common.errorGeneric')
      }
    }

    const cancel = () => {
      if (props.inPanel) emit('done')
      else navigationStore.goBack(router, '/home')
    }

    // ── Avatar crop ──
    const cropOpen = ref(false)
    const cropSrc = ref('')
    const stageRef = ref<HTMLDivElement | null>(null)
    const cropImgRef = ref<HTMLImageElement | null>(null)
    const cropBoxSize = ref(260)
    const zoomMultiplier = ref(1)
    let fitScaleBase = 1
    const imgScale = computed(() => fitScaleBase * zoomMultiplier.value)
    const imgX = ref(0)
    const imgY = ref(0)
    let dragStartX = 0, dragStartY = 0, dragStartImgX = 0, dragStartImgY = 0, isDragging = false

    watch(zoomMultiplier, (newMult, oldMult) => {
      const stage = stageRef.value
      if (!stage) return
      const cx = stage.clientWidth / 2
      const cy = stage.clientHeight / 2
      const oldScale = fitScaleBase * oldMult
      const newScale = fitScaleBase * newMult
      imgX.value = cx - (cx - imgX.value) * (newScale / oldScale)
      imgY.value = cy - (cy - imgY.value) * (newScale / oldScale)
    })

    const triggerAvatarInput = () => {
      const input = document.getElementById('avatar-file') as HTMLInputElement | null
      if (input) input.click()
    }

    const openCrop = (src: string, nw: number, nh: number) => {
      cropSrc.value = src
      cropOpen.value = true
      nextTick(() => {
        const stage = stageRef.value
        if (!stage) return
        const stageW = stage.clientWidth
        const stageH = stage.clientHeight
        const box = Math.min(stageW, stageH) * 0.72
        cropBoxSize.value = box
        fitScaleBase = Math.min(box / nw, box / nh)
        zoomMultiplier.value = 1
        imgX.value = (stageW - nw * fitScaleBase) / 2
        imgY.value = (stageH - nh * fitScaleBase) / 2
      })
    }

    const onAvatarSelected = (event: Event) => {
      const target = event.target as HTMLInputElement
      const file = target.files?.[0]
      if (!file || !file.type.startsWith('image/')) return
      target.value = ''
      const url = URL.createObjectURL(file)
      const img = new Image()
      img.onload = () => { openCrop(url, img.naturalWidth, img.naturalHeight) }
      img.src = url
    }

    const onStageDragStart = (e: MouseEvent) => {
      isDragging = true
      dragStartX = e.clientX; dragStartY = e.clientY
      dragStartImgX = imgX.value; dragStartImgY = imgY.value
      const onMove = (ev: MouseEvent) => {
        if (!isDragging) return
        imgX.value = dragStartImgX + (ev.clientX - dragStartX)
        imgY.value = dragStartImgY + (ev.clientY - dragStartY)
      }
      const onUp = () => { isDragging = false; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp) }
      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    }

    const onStageTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return
      const t0 = e.touches[0]
      dragStartX = t0.clientX; dragStartY = t0.clientY
      dragStartImgX = imgX.value; dragStartImgY = imgY.value
      const onMove = (ev: TouchEvent) => {
        if (ev.touches.length !== 1) return
        imgX.value = dragStartImgX + (ev.touches[0].clientX - dragStartX)
        imgY.value = dragStartImgY + (ev.touches[0].clientY - dragStartY)
      }
      const onEnd = () => { window.removeEventListener('touchmove', onMove); window.removeEventListener('touchend', onEnd) }
      window.addEventListener('touchmove', onMove)
      window.addEventListener('touchend', onEnd)
    }

    const cancelCrop = () => { cropOpen.value = false; cropSrc.value = '' }

    const confirmCrop = async () => {
      const stage = stageRef.value
      if (!stage) return
      const stageRect = stage.getBoundingClientRect()
      const stageW = stageRect.width, stageH = stageRect.height
      const boxLeft = (stageW - cropBoxSize.value) / 2
      const boxTop  = (stageH - cropBoxSize.value) / 2
      const srcX = (boxLeft - imgX.value) / imgScale.value
      const srcY = (boxTop  - imgY.value) / imgScale.value
      const srcSize = cropBoxSize.value / imgScale.value
      const canvas = document.createElement('canvas')
      canvas.width = CROP_OUTPUT_PX; canvas.height = CROP_OUTPUT_PX
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.onload = () => {
        ctx.drawImage(img, srcX, srcY, srcSize, srcSize, 0, 0, CROP_OUTPUT_PX, CROP_OUTPUT_PX)
        let quality = 0.88
        let result = canvas.toDataURL('image/jpeg', quality)
        while (result.length * 0.75 > TARGET_SIZE_BYTES && quality > 0.2) {
          quality -= 0.08
          result = canvas.toDataURL('image/jpeg', quality)
        }
        avatar.value = result
        cropOpen.value = false; cropSrc.value = ''
      }
      img.src = cropSrc.value
    }

    const carouselImages = [
      require('@/assets/login-1.svg'),
      require('@/assets/login-2.svg'),
      require('@/assets/login-3.svg'),
      require('@/assets/login-4.svg'),
      require('@/assets/login-5.svg')
    ]

    onMounted(loadProfile)

    return {
      activeTab, goTab,
      name, username, avatar, error,
      save, cancel,
      carouselImages,
      triggerAvatarInput, onAvatarSelected,
      cropOpen, cropSrc, stageRef, cropImgRef,
      cropBoxSize, imgScale, imgX, imgY, zoomMultiplier,
      onStageDragStart, onStageTouchStart,
      cancelCrop, confirmCrop,
    }
  }
})
</script>

<style scoped lang="scss">
.profile-shell {
  height: 100vh;
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: var(--bg);
  overflow: hidden;

  &--panel {
    height: 100%;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;
    overflow: hidden;
  }
}

.profile-left { position: relative; overflow: hidden; }

.profile-right {
  background: var(--surface);
  height: 100vh;
  overflow: hidden;
  border-left: 1px solid var(--border);

  &--panel {
    min-height: 0;
    overflow: hidden;
    border-left: none;
    background: var(--surface);
  }
}

.ps-wrap {
  display: grid;
  grid-template-columns: 220px 1fr;
  grid-template-rows: 1fr;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.ps-nav {
  border-right: 1px solid var(--border, rgba(31,30,26,.12));
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
  background: var(--bg);
}

.ps-user-card {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 20px 16px 18px;
  border-bottom: 1px solid var(--border, rgba(31,30,26,.1));
  cursor: pointer;
  transition: background .12s;
  &:hover { background: rgba(31,30,26,.03); }
}

.ps-ava { position: relative; width: 40px; height: 40px; flex-shrink: 0; }
.ps-ava-img { width: 40px; height: 40px; border-radius: 50%; object-fit: cover; }
.ps-ava-ph {
  width: 40px; height: 40px; border-radius: 50%;
  background: var(--system-navbar, #0f4c81); color: var(--system-navbar-text, #fff);
  display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 16px;
}
.ps-ava-badge {
  position: absolute; bottom: -1px; right: -1px; width: 16px; height: 16px;
  background: var(--system-navbar, #0f4c81); color: #fff; border-radius: 50%;
  display: flex; align-items: center; justify-content: center; border: 2px solid var(--surface, #fff);
}

.ps-user-info { flex: 1; min-width: 0; }
.ps-user-name { font-size: 13px; font-weight: 600; color: var(--ink); margin: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ps-user-handle { font-size: 12px; color: var(--ink-muted); margin: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.ps-tabs { padding: 8px 0; display: flex; flex-direction: column; }

.ps-tab {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 16px; background: none; border: none;
  border-right: 2px solid transparent; cursor: pointer;
  text-align: left; font-size: 13px; color: var(--ink-muted);
  transition: background .1s, color .1s;

  svg { flex-shrink: 0; opacity: .7; }
  &:hover { background: rgba(31,30,26,.04); color: var(--ink); }
  &--active {
    background: rgba(15, 76, 129, 0.07);
    color: var(--system-navbar, #0f4c81);
    font-weight: 600;
    border-right-color: var(--system-navbar, #0f4c81);
    svg { opacity: 1; }
  }
}

.ps-content {
  min-height: 0;
  overflow-y: auto;
  padding: 32px 40px;
  background: var(--surface);
  box-sizing: border-box;
}

.ps-content :deep(.btn-primary) {
  background: var(--system-navbar, #0f4c81);
  color: var(--system-navbar-text, #ffffff);
  border-color: transparent;
}
.ps-content :deep(.btn-primary:hover) { filter: brightness(1.1); }

.hidden-input { display: none; }

// ── CROP MODAL ──
.crop-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,.72);
  z-index: 9999; display: flex; align-items: center; justify-content: center;
}
.crop-modal {
  background: var(--surface); border-radius: 16px; padding: 24px;
  width: min(520px, 94vw); display: flex; flex-direction: column; gap: 16px;
  box-shadow: 0 24px 64px rgba(0,0,0,.4);
}
.crop-title { margin: 0; font-size: 18px; font-weight: 600; color: var(--ink); }
.crop-stage {
  position: relative; width: 100%; height: 340px; background: #111;
  border-radius: 10px; overflow: hidden; cursor: grab; user-select: none;
  &:active { cursor: grabbing; }
}
.crop-img { position: absolute; top: 0; left: 0; max-width: none; pointer-events: none; }
.crop-mask { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; pointer-events: none; }
.crop-box {
  position: relative; border-radius: 50%;
  box-shadow: 0 0 0 9999px rgba(0,0,0,.55);
  outline: 2px solid rgba(255,255,255,.85);
}
.crop-corner {
  position: absolute; width: 18px; height: 18px; border-color: #fff; border-style: solid;
  &.tl { top: -1px; left: -1px; border-width: 3px 0 0 3px; border-radius: 4px 0 0 0; }
  &.tr { top: -1px; right: -1px; border-width: 3px 3px 0 0; border-radius: 0 4px 0 0; }
  &.bl { bottom: -1px; left: -1px; border-width: 0 0 3px 3px; border-radius: 0 0 0 4px; }
  &.br { bottom: -1px; right: -1px; border-width: 0 3px 3px 0; border-radius: 0 0 4px 0; }
}
.crop-zoom-row { display: flex; align-items: center; gap: 12px; }
.crop-zoom-label { font-size: 13px; color: var(--ink-muted); white-space: nowrap; }
.crop-zoom-slider { flex: 1; accent-color: var(--accent); cursor: pointer; }
.crop-actions { display: flex; justify-content: flex-end; gap: 10px; }

.btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  border: 1px solid transparent; border-radius: var(--radius, 6px);
  padding: 6px 16px; font-size: 14px; font-weight: 600; cursor: pointer; transition: background .12s;
}
.btn-primary { background: var(--system-navbar, #0f4c81); color: var(--system-navbar-text, #fff); &:hover { filter: brightness(1.1); } }
.btn-ghost { background: transparent; color: var(--ink); border-color: var(--border-strong); &:hover { background: var(--surface-3); } }
.btn-login { padding: 10px 22px; border-radius: 8px; }

@media (max-width: 900px) {
  .profile-shell { grid-template-columns: 1fr; height: auto; overflow: visible; }
  .profile-left { height: 200px; }
  .profile-right { height: auto; overflow: visible; }
  .ps-wrap { grid-template-columns: 1fr; height: auto; min-height: 0; }
  .ps-nav { height: auto; border-right: none; border-bottom: 1px solid var(--border, rgba(31,30,26,.1)); }
  .ps-tabs { flex-direction: row; flex-wrap: wrap; padding: 4px; gap: 2px; }
  .ps-tab {
    border-right: none; border-bottom: 2px solid transparent;
    padding: 8px 12px; font-size: 12px; gap: 6px;
    svg { display: none; }
    &--active { border-right-color: transparent; border-bottom-color: var(--system-navbar, #0f4c81); }
  }
  .ps-content { height: auto; padding: 20px 16px; }
}
</style>
